import { injectable } from "inversify";
import { IRoute } from "./IRoute";
import { RouteFragment } from "./RouteFragment";
import { RouteRoot } from "./fragments/RouteRoot";
import { RouteCountry } from "./fragments/RouteCountry";
import { RouteContinent } from "./fragments/RouteContinent";
import { RouteArea } from "./fragments/RouteArea";
import { RouteAttendanceOnline } from "./fragments/RouteAttendanceOnline";
import { RouteRanking } from "./fragments/RouteRanking";
import { RouteDiscipline } from "./fragments/RouteDiscipline";

@injectable()
export class RouteBuilder {
	private readonly fragmentsInPriority: readonly RouteFragment[];

	public constructor() {
		this.fragmentsInPriority = [
			new RouteRanking(),
			new RouteAttendanceOnline(),
			new RouteCountry(),
			new RouteContinent(),
			new RouteArea(),
			new RouteDiscipline(),
		];
	}

	public build(): IRoute {
		const root = new RouteRoot();
		this.doBuildWithPruning(root, this.fragmentsInPriority);
		return root;
	}

	private doBuildWithPruning(currentNode: RouteFragment, fragmentsInPriority: readonly RouteFragment[]): void {
		for (const fragmentPrototype of fragmentsInPriority) {
			if (this.shouldPrune(currentNode, fragmentPrototype)) {
				continue;
			}

			const fragment = fragmentPrototype.instantiate();
			const remainingFragments = fragmentsInPriority.filter((p) => p !== fragmentPrototype);

			this.doBuildWithPruning(fragment, remainingFragments);
			currentNode.addChild(fragment);
		}

		currentNode.includeFallbacks();
	}

	private shouldPrune(currentNode: RouteFragment, nextFragment: RouteFragment): boolean {
		const pruningRules: Map<typeof RouteFragment, (typeof RouteFragment)[]> = new Map<typeof RouteFragment, (typeof RouteFragment)[]>([
			[RouteRoot.fragmentType, [RouteArea.fragmentType]],
			[RouteContinent.fragmentType, [RouteRanking.fragmentType, RouteAttendanceOnline.fragmentType, RouteDiscipline.fragmentType]],
			[RouteCountry.fragmentType, [RouteContinent.fragmentType]],
			[RouteArea.fragmentType, [RouteContinent.fragmentType, RouteCountry.fragmentType]],
			[RouteAttendanceOnline.fragmentType, [RouteRanking.fragmentType]],
			[RouteRanking.fragmentType, []],
			[RouteDiscipline.fragmentType, [RouteContinent.fragmentType, RouteRoot.fragmentType]],
		]);

		const currentClassType = currentNode.constructor as typeof RouteFragment;
		const nextClassType = nextFragment.constructor as typeof RouteFragment;

		return pruningRules.get(currentClassType)?.includes(nextClassType) ?? false;
	}

}
